export default {
  app: {
    title: "FaceID Admin",
    welcome: "Welcome",
  },
  nav: {
    dashboard: "Dashboard",
    companies: "Companies",
    sessions: "Sessions",
    analytics: "Analytics",
    funnel: "Funnel",
    verdicts: "Verdicts",
    performance: "Performance",
    tenants: "Tenants",
    sdkFunnel: "SDK Funnel",
    sdkDevices: "SDK Devices",
    uploadRejections: "Upload Rejections",
    identities: "Identities",
    mobile: "Mobile",
    mobileUsers: "Users",
    mobileDevices: "Devices",
    mobileAccessEvents: "Access events",
    mobileQrRequests: "QR requests",
    catalog: "Catalog",
    catalogServices: "Services",
    catalogCategories: "Categories",
    apiLogs: "API Logs",
    auditLogs: "Audit Logs",
    users: "Users",
    settings: "Settings",
  },
  auth: {
    loginTitle: "Sign in",
    email: "Email",
    password: "Password",
    totpCode: "2FA code",
    loginBtn: "Sign in",
    logout: "Log out",
    invalidCredentials: "Invalid email or password",
    needTotp: "TOTP code required",
  },
  dashboard: {
    title: "Overview",
    todaySessions: "Sessions today",
    totalSessions: "Total sessions",
    successRate: "Success rate",
    abandonRate: "Abandoned",
    activeCompanies: "Active companies",
    avgResponseTime: "Avg. response time",
    recentActivity: "Recent activity",
    weeklyChart: "Sessions — last 7 days",
    passed: "Passed",
    failed: "Failed",
    abandoned: "No verdict",
    ofTerminal: "{passed} of {terminal} completed",
    ofAllSessions: "{pct} of all {total} sessions",
    noVerdict: "{n} sessions reached no verdict",
    outcomeMix: "How sessions ended — last 7 days",
    outcomeMixHint:
      "Every session in the window, by the status it holds now, so the segments cover all of them. Expired is the swept bucket and holds two different things: no photo ever arrived, and a photo arrived, was rejected and nobody came back. Telling those apart needs the attempt count, which this summary does not carry — the Funnel board computes that split from the attempts themselves.",
    mix: {
      passed: "Passed",
      failed: "Rejected",
      inProgress: "Photo sent, no verdict yet",
      pending: "No photo sent yet",
      expired: "Expired without a verdict",
      cancelled: "Cancelled",
      other: "Other status",
    },
  },
  analytics: {
    funnelTitle: "Funnel — where verifications fall off",
    verdictsTitle: "Verdicts — what the decision rested on",
    sessionFunnel: "Session funnel",
    gateFunnel: "By gate (attempts)",
    abandonment: "How sessions end without a verdict",
    abandonmentHint: "The difference is exactly one thing: whether a photo arrived. The first opened the SDK and sent nothing — an SDK/UX problem. In the second a photo did arrive, was rejected, and the person never came back. The third is the only real refusal: every attempt used up.",
    retryCurve: "Pass rate by attempt",
    retryHint: "Does retrying help? The session keeps only its latest verdict, so this reads from the attempt rows.",
    stages: {
      created: "Session created",
      frames_submitted: "Frames submitted",
      verdict_reached: "Reached a verdict",
      passed: "Passed",
    },
    abandon: {
      neverStarted: "No photo ever arrived",
      gaveUp: "Photo arrived, rejected — never came back",
      exhausted: "All attempts used up",
    },
    gates: {
      detection: "Face detection",
      liveness: "Liveness (passive)",
      challenge: "Challenge",
      deepfake: "Deepfake",
      match: "Match",
    },
    gate: "Gate",
    reached: "Reached",
    gatePassed: "Passed",
    gateRejected: "Rejected",
    gateSkipped: "Not run",
    rejectionRate: "Rejection rate",
    attemptNo: "Attempt",
    attempts: "Total",
    passed: "Passed",
    notPassed: "Did not pass",
    passRate: "Pass rate",
    noAttemptsTitle: "No attempt data",
    noAttemptsHint: "The attempts table is only populated after migration 0016. Zero means \"nothing recorded yet\", not \"nobody was rejected\".",
    ofTotal: "{pct} of total",
    lostHere: "{n} lost here ({pct})",
    similarityTitle: "Similarity distribution",
    livenessTitle: "Liveness — composite score",
    livenessMedianTitle: "Liveness — median (this is what the gate judges)",
    livenessMedianHint: "The {gate} threshold applies to the median. Only attempts where this gate ran, split by its own verdict.",
    livenessCompositeHint: "The blended score stored on the session (0.55·median + 0.30·min + 0.15·variance). No threshold is ever applied to it, which is why no gate line is drawn here.",
    noAttemptScores: "No attempt scores recorded yet",
    noAttemptScoresHint: "The median comes from attempts.scores, which is populated after migration 0016.",
    candidateThreshold: "Candidate threshold",
    thresholdMark: "In force",
    candidateMark: "Candidate",
    thresholdScope: "The slider recomputes the MATCH decision only. Sessions rejected earlier (spoof, challenge, no govt photo) never produced a similarity, and a lower threshold cannot rescue them.",
    scoredSessions: "Sessions with a score",
    currentlyPassed: "Currently passed",
    wouldPass: "At the new threshold",
    netChange: "Net change",
    nearThreshold: "{n} sessions within ±{band} of their threshold",
    nearThresholdHint: "These are the ones a one-step move would flip.",
    thresholdDrift: "This selection spans several thresholds — sessions were judged by different rules",
    medianGate: "Median gate",
    failuresOverTime: "Failure reasons over time",
    spoofBasis: "What the spoof verdict rested on",
    spoofSamples: "Rejections",
    spoofMedian: "Average median",
    spoofRange: "Median range",
    spoofVariance: "Average variance",
    spoofFrames: "Average frames",
    required: "required",
    noSpoofData: "No spoof detail",
    noSpoofDataHint: "These numbers come from attempts.failure_details, which is written after migration 0016.",
    noScores: "No scored sessions",
    groupedNote: "Bars grouped at {width} wide",
    dateFrom: "From",
    dateTo: "To",
    percentilesApprox: "The p-values in this panel are estimated from hourly histograms, so each is only as accurate as the latency bucket it falls in (marked ≈). Anything at or above {cap} is reported as a floor — the histogram does not resolve past it.",
    reset: "Reset",
  },
  forensics: {
    title: "Session forensics",
    backToList: "Back to sessions",
    attempt: "Attempt {n}",
    frames: "{n} frames",
    afterGap: "{s}s after the previous",
    gateTable: "Gates — what was measured, what was required",
    timings: "Stage timings",
    frameStrip: "Frames",
    noFrameData: "No frame diagnostics for this attempt",
    noAttempts: "No attempt records",
    noAttemptsHint: "This session finished before the attempts table started being written (migration 0016), or no frames were ever submitted.",
    imagesGone: "Images are kept only for the most recent attempt — each submission overwrites them. The numbers are complete for every attempt.",
    imagesMismatch: "The stored image count does not match what this attempt submitted — they belong to an earlier one, so they are not shown.",
    indeterminate: "The stored value is within rounding distance of the threshold — this line cannot be resolved",
    noFace: "no face",
    multiFace: "multiple faces",
    thresholdFromConfig: "Read from the current configuration — it may have changed since this attempt",
    thresholdFromSession: "Snapshotted when the session was created — this is the value that judged the attempt",
    configLabel: "config",
    sessionLabel: "exact",
    byPinfl: "Other sessions for this PINFL",
    byIp: "Other sessions from this IP",
    openShort: "Open",
    criteria: {
      median: "median",
      minimum: "minimum (trimmed)",
      variance: "variance",
      samples: "frame count",
      similarity: "similarity",
      deepfake: "deepfake",
      frames_with_face: "frames with a face",
      frames_with_multiple_faces: "frames with several faces",
    },
    state: {
      passed: "passed",
      rejected: "rejected",
      not_run: "not run",
    },
  },
  perf: {
    scopedHeadroom: "Note: a filter is active — the peak is for the selected slice while the capacity is fleet-wide. Spare headroom here does not mean the fleet has any.",
    title: "Performance — how fast, and how much is left",
    headroom: "Load and headroom",
    peakMinute: "Busiest minute",
    peakRps: "Peak rate",
    perSecond: "per second",
    capacity: "Measured capacity",
    measuredOn: "measured {d}",
    spare: "Spare headroom",
    capacityHint: "Capacity is a load-test measurement, not a specification. Re-measure it after any change to worker count, MAX_INFLIGHT or the GPU. Taken per minute: an hourly average flattens exactly the spike that would breach the ceiling.",
    whichBackend: "Which backend served",
    backendHint: "A GPU that quietly degrades to the CPU shows up only as latency. Hence its own column. «none» means the session never reached analysis.",
    stageTitle: "Where the time goes (inside an attempt)",
    stage: "Stage",
    stages: {
      signature: "Signature check",
      analyze: "Model analysis",
      challenge: "Challenge",
      deepfake: "Deepfake",
      govt_photo: "Govt photo (IMV)",
      match: "1:1 match",
    },
    samples: "Samples",
    avg: "Average",
    p50: "p50",
    p95: "p95",
    p99: "p99",
    noStageData: "No stage timings recorded yet",
    noStageDataHint: "This comes from attempts.stage_durations, populated by verifications after migration 0016.",
    endpoints: "Endpoint latency",
    endpoint: "Endpoint",
    endpointScope: "Fleet-wide — it cannot be split by application: api_logs does not carry application_id (until Sprint 9). With no date filter, the last 7 days are read.",
    calls: "Calls",
    errorRate: "Error rate",
    slowest: "Slowest sessions",
    duration: "Duration",
  },
  tenants: {
    noErrorRate: "* No API error rate: api_logs carries no application attribution (until Sprint 9). Dropping the column is honest; a permanent 0.0% is not.",
    quotaScope: "Quota is always calendar-month-to-date and ignores the date filter.",
    title: "Tenants — who uses it, and how well it works for them",
    application: "Application",
    sessions: "Sessions",
    passRate: "Pass rate",
    abandonRate: "Abandoned",
    quota: "Quota",
    projected: "projected {n}",
    projectionHint: "Straight-line projection to month end: {n}. A hint about hitting the quota, not a forecast.",
    rules: "Rules in force",
    spoofOff: "spoof off",
    livenessOff: "liveness off",
    outlierTitle: "{n} applications sit well below the fleet",
    outlierBody: "{names} — fleet mean {mean}, floor {floor}. A gap like this is usually integration or capture quality, not the model.",
    thresholdDrift: "Several thresholds are in force across the fleet: {list}",
    thresholdDriftHint: "The same face can pass in one application and fail in another. This has shipped unnoticed before.",
    fleetNote: "Fleet mean {mean} across {n} applications. Only applications with at least {min} terminal sessions count — a small sample is noise, and flagging noise devalues the flag.",
    noFleet: "Not enough applications to compare — nothing is flagged.",
  },
  sdk: {
    funnelTitle: "SDK funnel — where the phone lost them",
    devicesTitle: "SDK devices — what they were running on",
    platform: "Platform",
    platforms: {
      android: "Android",
      ios: "iOS",
    },
    bundleId: "App bundle id",
    sdkVersion: "SDK version",
    nFlows: "{n} flows",
    testTraffic: "Test traffic",
    testTrafficHint: "Emulator and debuggable builds. Excluded by default — otherwise internal testing sits inside production numbers.",
    window: "Window (server clock)",
    received: "Flows received",
    counted: "Counted",
    countedHint: "Every share on this page is divided by it",
    excluded: "Excluded",
    excludedBreakdown: "emulator {emulator} · debuggable {debuggable}",
    excludedOverlap: "The total is not the sum of the two: a debuggable build on an emulator is one row, and counting it twice would push the exclusion above the population it came from.",
    testTrafficCounted: "Test traffic inside",
    includedOverlap: "Test traffic is included in the numbers. The emulator and debuggable counters overlap — one row can be both — so they cannot be added into a total.",
    clientScope: "Client: {id}",
    testTrafficOn: "Test traffic included",
    empty: {
      never: "No SDK telemetry has ever arrived",
      neverHint: "This table starts filling once the Android SDK build carrying schema_version 3 ships. Drawing 0% here would present the unmeasured as measured, so no panel is drawn.",
      window: "No flows in this window",
      windowHint: "Telemetry has arrived, just not within the selected dates. Widen the range.",
      testOnly: "Everything in this window was test traffic",
      testOnlyHint: "{n} flows came from an emulator or a debuggable build and are excluded from production numbers. Turn on the Test traffic switch above to see them.",
    },
    pairTitle: "Two halves: what the phone saw, and what the server saw",
    pairHint: "The server can only see a session once it exists, and 54.3% of sessions are created and never receive a photo — server side that is silence. The eight stages above are what the phone itself knew; this is the first page where both halves stand together.",
    clientHalf: "Phone side (SDK)",
    serverHalf: "Server side (sessions)",
    serverHalfHint: "This half is every session in the same date range; when a client filter is set it applies to both halves. Platform, SDK version and bundle id do not exist on a session at all, so they narrow only the phone side. How far the two overlap is counted by the join panel below.",
    pairWaiting: "The phone side is empty — the SDK is not sending telemetry yet.",
    unstaged: "{n} flows sit outside the eight (REGISTRATION and QR_LOGIN, which have their own stages). They are kept out of LAUNCHED: folding them in would inflate the top of the funnel with people who never entered it.",
    stages: {
      LAUNCHED: "SDK launched",
      SESSION_CREATED: "Session created",
      CENTERING: "Centring the face",
      FACE_DETECTED: "Face detected",
      FRAMED: "Framed in the oval",
      CHALLENGE: "Gesture asked",
      PROCESSING: "Submitted, waiting",
      RESULT_SHOWN: "Result shown",
    },
    joinTitle: "Where it meets a session",
    joinHint: "A LEFT join, and it must stay one: the sessionless flows are the reason this table exists — camera permission denied, session mint failed. An INNER join would delete exactly them and leave the same blind spot in a new table.",
    join: {
      matched: "Session found",
      sessionless: "Never had a session",
      unmatched: "Session id present, not found",
      matchedPassed: "Server: passed",
      matchedFailed: "Server: rejected",
      matchedOpen: "Server: no verdict",
    },
    matchedHint: "Over the flows whose session was found.",
    abandonTitle: "Where they walked away",
    abandonHint: "{n} flows were abandoned in total. A long p90 on PROCESSING is the clearest I-thought-it-had-frozen signal there is, not a slow server.",
    abandonWhere: "Count by stage",
    abandonHowLong: "How long they had waited",
    abandonedFlows: "Abandoned flows",
    stage: "Stage",
    ofAbandoned: "Of abandoned",
    samples: "Samples",
    samplesHint: "Samples is the percentile denominator and is not the flow count: a flow can record where it stopped without recording how long it sat there. Where nobody reported one, the cell is a dash, not 0.",
    errorsTitle: "What broke (error_kind)",
    errorsHint: "The share is of every counted flow: 3% of all flows died on camera permission is the sentence somebody acts on.",
    errorKind: "Error class",
    ofFlows: "Of flows",
    noErrors: "No errors recorded",
    chartTopN: "The chart shows the largest {shown}; the table holds all {total}.",
    errorKindsCut: "The window held {total} distinct error kinds; the list shows the largest {shown}. error_kind is meant to be low cardinality, so a count near the cap is itself the finding — an SDK build may be inventing class names.",
    retryTitle: "Retry — did they ever see the offer?",
    retryHint: "Measured server side: 76% of abandoners quit after ONE rejection while still holding two attempts. A second row here is proof somebody actually saw the retry offer. The Sessions column counts distinct session ids — a sessionless flow has nothing to come back to.",
    attempt: "Attempt",
    attemptN: "Attempt {n}",
    noAttempts: "No attempt rows",
    flows: "Flows",
    sessions: "Sessions",
    reachedResult: "Reached result",
    abandoned: "Abandoned",
    abandonRate: "Abandonment rate",
    passed: "Passed",
    p50: "p50",
    p90: "p90",
    p99: "p99",
    timingTitle: "The wait, and the network share of it",
    timingHint: "processing_ms is the whole time the spinner was up — the wait a person actually feels. upload_ms splits the network out of it and is optional even at schema_version 3: absent means not reported, never 0 ms. That is why each carries its own sample count.",
    processing: "Processing (spinner)",
    upload: "Upload (network)",
    processingP50: "Processing p50",
    processingP90: "Processing p90",
    processingP99: "Processing p99",
    uploadP50: "Upload p50",
    uploadP90: "Upload p90",
    cancelled: "Cancelled while processing",
    cancelledOf: "{n} of {total}",
    nSamples: "{n} samples",
    noUpload: "No flow reported an upload time — that is not 0 ms, it is no measurement at all.",
    byDeviceTitle: "Worst devices (Build.DEVICE)",
    byDeviceHint: "Build.DEVICE is the board codename: one model, and it survives marketing renames.",
    byHardwareTitle: "Worst chipsets (Build.HARDWARE)",
    byHardwareHint: "Build.HARDWARE is the SoC — a whole chipset family. This model is broken and every phone on this chipset is broken are different bugs with different owners.",
    device: "Device (DEVICE)",
    hardware: "Chipset (HARDWARE)",
    droppedRatio: "Dropped frames",
    frameSamples: "Frame samples",
    deviceModels: "Models",
    unknownDevice: "No device block arrived",
    noGroups: "No group cleared the {n}-flow threshold",
    suppressed: "Threshold: at least {min} flows per group. Below it, {groups} groups holding {flows} flows were left out — a rate over a handful of events is noise, but this total is what reveals the broad problem of a hundred models each failing five times.",
    topN: "Showing {shown} of {total}",
    droppedTitle: "Dropped-frame ratio (by device)",
    droppedHint: "A high ratio on one model is a camera or thermal fault. Server side it surfaces as INSUFFICIENT_FACE_FRAMES and reads as the user failing.",
    noFrameCounts: "No group reported frame counts",
    droppedMissing: "{n} groups reported no frame counts at all — that is not 0%, it is no measurement.",
    apiLevelTitle: "By API level",
    apiLevelHint: "Build.VERSION.SDK_INT is more useful than the marketing version: camera and ML behaviour tracks the API level. iOS has no such field, and an Android flow may have arrived without its device block — neither is 0, both are not measured.",
    apiLevel: "API level",
    densityTitle: "Screen density against oval flapping",
    densityHint: "framing_flap_count is the bobbing-in-and-out-of-the-oval pathology. Spread evenly across densities it is a guidance problem; concentrated in one dpi band the oval is laid out at the wrong size on those screens — fixable UI work.",
    density: "Density (dpi)",
    flapSamples: "Flap samples",
    flapSamplesHint: "The average and p90 count only the flows that reported a value — which is why the sample count, not the flow count, sits beside them.",
    avgFlaps: "Average flaps",
    p90Flaps: "p90 flaps",
    integratorsTitle: "Integrators (host apps)",
    integratorsHint: "Keyed on app_bundle_id, not application_id: nothing writes Application.bundle_ids yet, so application_id is NULL on every row. The bundle id is caller-supplied — it segments host apps, it does not attribute them.",
    bundleUnverified: "No client filter is set: the bundle id is caller-supplied, so anyone holding a valid SDK token can file flows under someone else's bundle. These rows are indicative, not authoritative. Filtered by tenant, what remains under a bundle is what that tenant actually sent.",
    sdkVersions: "SDK versions",
    versionClickHint: "Clicking a version tag filters the whole page — devices, screens, timings — to that build. The abandonment rate per version is in the Releases table below, without filtering anything.",
    releasesTitle: "Releases — did the new version make it worse?",
    releasesHint: "One row per app and SDK build, with builds sitting side by side in release order. The last column is the difference from that app's previous build in this list, in percentage points: a plus means the new release is losing more people. Note that builds below the threshold never enter the list, so previous means previous shown, not necessarily the release immediately before.",
    version: "Version",
    deltaVsPrev: "vs previous version",
    ppSuffix: "pp",
    baselineVersion: "first build",
    releaseSuppressed: "Below the threshold: {groups} (app, version) pairs holding {flows} flows. A freshly rolled-out version always starts here, and its absence does not mean zero.",
    noReleases: "No version rows to compare",
    versionsTitle: "Versions comparison — how each build behaves",
    versionsHint: "One row per SDK build, grouped by platform and never merged across them — the same version string exists on both. Click a version to filter the whole board by that build. Sortable, unlike the releases table on the devices board: these rows stand alone, nothing is compared against the row above.",
    versionsEdgeHint: "A build's first and last day inside the window are partial, so a version that only just rolled out carries few flows — read the flow count before reading its rates.",
    share: "Share",
    passRate: "Pass rate",
    errorFlows: "Errors",
    topError: "Most common error",
    firstSeen: "First seen",
    lastSeen: "Last seen",
    noVersions: "No version rows in this window",
    notMeasured: "Not measured",
    shareHint: "Of this platform's flows in the window, not of the whole window: the shares inside one platform add up to 100%. Android and iOS are two populations and are never pooled.",
    mixedPlatforms: "The platform filter is empty: Android and iOS are on one page. The rows never merge, but do not compare them — most of what is measured here does not exist on iOS at all.",
  },
  rejections: {
    title: "Rejected uploads — what the server turned away",
    sessionTitle: "Rejected uploads",
    empty: "No rejected uploads",
    noData: "No uploads in this window",
    loadFailed: "Rejected uploads could not be loaded",
    time: "Time",
    status: "Status",
    category: "Reason",
    platform: "Platform",
    duration: "Duration",
    uploads: "Uploads",
    rejected: "Rejected",
    rejectedShare: "Rejected share",
    dailyTitle: "Per day — uploads against rejections",
    categoriesTitle: "Reasons",
    platformsTitle: "By platform",
    count: "Count",
    share: "Share",
    rangeHint: "Up to {n} days",
    rangeClamped: "The window is capped at {n} days — the start date was moved up",
    noCategory: "No category recorded",
    httpCode: "HTTP {status}",
    endpoint: "Endpoint",
    sessionScope: "Requests from the device that were refused — not only frame uploads.",
    codes: {
      FRAME_TAMPERED: "Frame tampered with",
      INVALID_TOKEN: "Invalid token",
      INVALID_IMAGE: "Invalid image",
      FRAMES_TOO_MANY: "Too many frames",
      FRAMES_TOO_FEW: "Too few frames",
      SESSION_EXPIRED: "Session expired",
      SESSION_NOT_FOUND: "Session not found",
      ATTESTATION_FAILED: "App attestation failed",
      CLIENT_DISCONNECT: "Client disconnected",
      VALIDATION_ERROR: "Invalid request format",
    },
    details: {
      body_hash: "Body hash mismatch",
      clock_skew: "Device clock is off",
      hmac_mismatch: "Signature mismatch",
      missing_bearer: "No bearer token sent",
      not_sdk_token: "Not an SDK token",
      wrong_session: "Token for another session",
      bad_subject: "Token subject is not a session",
      unknown_session: "Token names no session",
      bad_payload_json: "Malformed JSON body",
      frame_too_large: "A frame is too large",
      total_too_large: "Upload total too large",
      part_too_large: "A capture part is too large",
      metadata_too_large: "Metadata too large",
      too_many: "Too many frames",
      missing_proof: "A frame arrived with no proof",
      too_few: "Too few frames",
      too_many_files: "Too many files",
      bad_metadata_json: "Malformed metadata JSON",
      metadata_too_deep: "Metadata nested too deep",
      missing_flash_id: "Flash id missing",
      not_jpeg: "Not a JPEG",
      body_read: "Upload broke off midway",
      no_route: "No such route",
    },
    platforms: {
      android: "Android",
      ios: "iOS",
      web: "Web",
      other: "Other",
    },
  },
  companies: {
    title: "Companies",
    create: "New company",
    name: "Name",
    slug: "Slug",
    slugHint: "a-z, 0-9, -",
    inn: "INN",
    email: "Email",
    phone: "Phone",
    address: "Address",
    status: "Status",
    createdAt: "Created",
    actions: "Actions",
    edit: "Edit",
    suspend: "Suspend",
    activate: "Activate",
    detail: "Details",
    applications: "Applications",
    statusActive: "active",
    statusSuspended: "suspended",
    statusPending: "pending verification",
    createSuccess: "Company created",
    updateSuccess: "Company updated",
  },
  sessions: {
    title: "Sessions",
    id: "Session ID",
    method: "Method",
    status: "Status",
    statusHint:
      "The label is derived from the attempt count, not the status word: 0 attempts means no photo ever arrived, 1 or more means a photo arrived and was rejected. Hover to see the raw status.",
    outcome: {
      passed: "Passed",
      failed: "Rejected",
      cancelled: "Cancelled",
      awaitingPhoto: "Awaiting photo",
      retrying: "Photo sent, retrying",
      expiredNoPhoto: "Expired — no photo arrived",
      expiredAbandoned: "Expired — abandoned after rejection",
    },
    pinfl: "PINFL",
    similarity: "Similarity",
    liveness: "Liveness",
    quality: "Quality",
    attempts: "Attempts",
    failureReason: "Failure reason",
    createdAt: "Created",
    completedAt: "Completed",
    ip: "IP address",
    detail: "Session details",
    company: "Company",
    application: "Application",
    origin: "Origin",
    threshold: "Threshold",
    mlBackend: "ML backend",
    mlBackendNone: "Not analysed",
    duration: "Duration",
    unmanaged: "Unmanaged",
    thresholdDiffers: "Differs from most rows on this page",
    thresholdDrift: "{count} different thresholds used in this filter",
    thresholdDriftHint: "Across the whole filter, not just the current page",
    allStatuses: "All statuses",
    allMethods: "All methods",
    allCompanies: "All companies",
    allApplications: "All applications",
    allOrigins: "All origins",
    allFailureReasons: "All failure reasons",
    allBackends: "All backends",
    origin_sdk: "SDK",
    origin_app_login: "App login",
    origin_qr_login: "QR login",
    dateRange: "Date range",
    searchPlaceholder: "Search by PINFL",
    resetFilters: "Reset filters",
    clientScope: "Client: {id}",
    statsTotal: "Total",
    statsPassed: "Passed",
    statsFailed: "Failed",
    passRate: "Pass rate",
    passRateHint: "Terminal sessions only: passed / (passed + failed)",
    avgSimilarity: "Avg similarity",
    avgLiveness: "Avg liveness",
    avgDuration: "Avg duration",
    empty: "No sessions found",
    forbidden: "You don't have access to this data",
    photos: "Photos",
    govtPhoto: "Passport photo",
    registryPassportPhoto: "Passport photo (Identities)",
    selfie: "Selfie",
    frames: "Frames",
    noPhotos: "No photos for this session",
  },
  // Application security checks — worded once, used by both the
  // applications table and the sessions table.
  challenges: {
    head_yaw_left: "Turn left",
    head_yaw_right: "Turn right",
    head_pitch_up: "Look up",
    head_pitch_down: "Look down",
  },
  checks: {
    title: "Checks",
    spoof: "Spoof check",
    liveness: "Liveness check",
    frameTime: "Frame time check",
    appAttest: "iOS App Attest check",
    stateOn: "on",
    stateOff: "off",
    disabled: "Check disabled",
    disabledHint: "Disabled: {checks}",
    disabledHintSession: "Disabled for this session: {checks}",
    superAdminOnly: "Only a super admin can change the checks",
  },
  identities: {
    title: "Verified identities",
    searchPlaceholder: "Search by PINFL or name",
    pinfl: "PINFL",
    fullName: "Full name",
    firstName: "First name",
    lastName: "Last name",
    middleName: "Middle name",
    passport: "Passport",
    birthDate: "Birth date",
    verificationCount: "Verifications",
    lastVerifiedAt: "Last verified",
    createdAt: "Created",
    updatedAt: "Updated",
    images: "Images",
    passportImage: "Passport photo",
    verificationImage: "Verification frame",
    noImage: "No image",
    detail: "Identity details",
    empty: "No verified identities yet",
    delete: "Delete identity",
    deleteConfirm:
      "Permanently delete the identity {pinfl}? The record and BOTH image files are erased. This cannot be undone.",
    deleteSuccess: "Identity deleted",
    forbidden: "This page is available to super admins only",
  },
  mobile: {
    usersTitle: "Mobile app users",
    usersSearchPlaceholder: "PINFL, name or phone",
    usersEmpty: "No mobile users yet",
    devicesTitle: "Mobile devices",
    devicesSearchPlaceholder: "Device ID, model or owner",
    devicesEmpty: "No devices",
    eventsTitle: "Access events",
    eventsEmpty: "No events",
    qrTitle: "QR requests",
    qrEmpty: "No QR requests",
    pinfl: "PINFL",
    fullName: "Full name",
    firstName: "First name",
    lastName: "Last name",
    middleName: "Middle name",
    passport: "Passport",
    birthDate: "Birth date",
    phone: "Phone",
    status: "Status",
    allStatuses: "All statuses",
    status_active: "active",
    status_suspended: "suspended",
    status_deleted: "deleted",
    status_revoked: "revoked",
    status_pending: "pending",
    status_scanned: "scanned",
    status_approved: "approved",
    status_rejected: "rejected",
    status_expired: "expired",
    status_consumed: "consumed",
    deviceCount: "Devices (total)",
    loginCount: "Events",
    lastLogin: "Last login",
    lastEventAt: "Last event",
    createdAt: "Created",
    updatedAt: "Updated",
    language: "Language",
    userDetail: "User details",
    tabProfile: "Profile",
    tabDevices: "Devices",
    tabHistory: "History",
    photo: "Enrolled photo",
    noPhoto: "No image",
    owner: "Owner",
    platform: "Platform",
    allPlatforms: "All platforms",
    platform_ios: "iOS",
    platform_android: "Android",
    model: "Model",
    osVersion: "OS version",
    deviceUid: "Device ID",
    lastSeen: "Last seen",
    revoke: "Revoke",
    revokeDevice: "Revoke device",
    revokeConfirm:
      "Revoke device {device}? It is logged out immediately and has to enroll again.",
    revokeSuccess: "Device revoked",
    suspend: "Suspend",
    activate: "Activate",
    suspendConfirm:
      "Suspend user {pinfl}? They are logged out of all devices immediately.",
    activateConfirm: "Activate user {pinfl} again?",
    suspendSuccess: "User suspended",
    activateSuccess: "User activated",
    time: "Time",
    eventType: "Event type",
    allEventTypes: "All types",
    event_app_login: "App login",
    event_qr_login: "QR login",
    result: "Result",
    allResults: "All results",
    result_approved: "approved",
    result_rejected: "rejected",
    result_failed: "failed",
    systemName: "System",
    similarity: "Similarity",
    liveness: "Liveness",
    ip: "IP address",
    dateFrom: "From",
    dateTo: "To",
    scopes: "Requested scopes",
    scannedAt: "Scanned",
    completedAt: "Completed",
    expiresAt: "Expires",
    statsTitle: "Mobile app",
    totalUsers: "Total users",
    activeDevices: "Active devices",
    loginsToday: "Logins today",
    qrApprovalsToday: "QR approvals today",
    forbidden: "This page is available to super admins only",
  },
  catalog: {
    servicesTitle: "Service catalog",
    servicesEmpty: "No services found",
    searchPlaceholder: "Search by name",
    createService: "New service",
    editService: "Edit service",
    logo: "Logo",
    logoUrl: "Logo URL",
    logoDropHint: "Drop a file here or click to choose one",
    logoConstraints: "PNG, JPEG or WEBP · up to 2 MB · resized to 512×512",
    logoTypeRejected: "Only PNG, JPEG or WEBP images can be uploaded",
    logoTooLarge: "The file must not exceed 2 MB",
    logoUploadSuccess: "Logo uploaded",
    logoFromUpload: "Uploaded file",
    logoFromUrl: "External URL",
    logoRemove: "Remove logo",
    logoUseUrl: "or paste a URL",
    name: "Name",
    category: "Category",
    allCategories: "All categories",
    type: "Type",
    allTypes: "All types",
    type_web: "Web page",
    type_mobile: "Mobile app",
    link: "Link",
    link_ios: "iOS",
    link_android: "Android",
    link_deeplink: "Deeplink",
    noLink: "No link",
    url: "Web page URL",
    urlRequired: "A web page needs a URL",
    invalidUrl: "URL must start with http:// or https://",
    iosUrl: "iOS link (App Store)",
    androidUrl: "Android link (Google Play)",
    deeplink: "Deeplink",
    mobileLinksHint:
      "Fill at least one: iOS, Android or deeplink — otherwise the app cannot open the card.",
    mobileLinkRequired:
      "At least one link is required: iOS, Android or deeplink",
    company: "Company",
    companyPlaceholder: "Optional",
    order: "Order",
    orderHint: "Lower is shown first",
    active: "Active",
    allStatuses: "All statuses",
    statusActive: "Active",
    statusInactive: "Inactive",
    resetFilters: "Reset filters",
    noCategories: "Create a category first — a service can't be saved without one.",
    createServiceSuccess: "Service created",
    updateServiceSuccess: "Service updated",
    deleteService: "Delete service",
    deleteServiceConfirm:
      "Delete service {name}? It disappears from the mobile app.",
    deleteServiceSuccess: "Service deleted",
    categoriesTitle: "Service categories",
    categoriesEmpty: "No categories",
    createCategory: "New category",
    editCategory: "Edit category",
    key: "Key",
    keyHint: "a-z, 0-9, -, _",
    keyInvalid: "Key: a-z, 0-9, - and _, 2–64 chars",
    keyImmutable: "The key can't be changed after creation",
    nameUz: "Name (uz)",
    nameRu: "Name (ru)",
    nameEn: "Name (en)",
    createCategorySuccess: "Category created",
    updateCategorySuccess: "Category updated",
    deleteCategory: "Delete category",
    deleteCategoryConfirm: "Delete category {name}?",
    deleteCategorySuccess: "Category deleted",
    deleteCategoryBlocked: "Category not deleted",
    categoryInUseHint:
      "Move the services in this category elsewhere or delete them first.",
    forbidden: "You don't have permission to change this data",
  },
  apiLogs: {
    title: "API Logs",
    endpoint: "Endpoint",
    method: "Method",
    statusCode: "Status",
    duration: "Duration",
    success: "Result",
    errorCode: "Error code",
    ip: "IP address",
    cached: "Cached",
    timestamp: "Time",
    allCodes: "All codes",
    onlySuccess: "Success only",
    onlyFailed: "Failed only",
  },
  auditLogs: {
    title: "Audit Logs",
    eventType: "Event type",
    actorType: "Actor",
    actorId: "Actor ID",
    ip: "IP address",
    payload: "Payload",
    createdAt: "Time",
    allEvents: "All events",
    allActors: "All actors",
    view: "View",
    detail: "Audit entry",
  },
  members: {
    title: "Members",
    invite: "Invite member",
    email: "Email",
    role: "Role",
    fullName: "Full name",
    createdAt: "Joined",
    roleOwner: "Owner",
    roleAdmin: "Admin",
    roleViewer: "Viewer",
    roleHint:
      "All three give the same access today: this company's data, read-only. The role is recorded for later.",
    changeRole: "Change role",
    remove: "Remove",
    removeConfirm: "Remove this member?",
    inviteSuccess: "Member added",
    roleSuccess: "Role updated",
    removeSuccess: "Member removed",
    tempPasswordWarning: "A new user was created. This password is shown only NOW!",
    tempPassword: "Temp password",
  },
  applications: {
    title: "Applications",
    create: "New application",
    name: "Name",
    slug: "Slug",
    clientId: "client_id",
    clientSecret: "client_secret",
    webhookSecret: "Webhook secret",
    webhookSecretHint:
      "HMAC key to verify the X-FaceID-Signature header on webhooks.",
    rotateWebhookSecret: "Rotate secret",
    rotateConfirm:
      "Rotate this application's webhook secret? The old secret stops working immediately.",
    webhookSecretRotated: "New webhook secret",
    rotateClientSecret: "New client_secret",
    rotateClientConfirm:
      "Rotate this application's client_secret? The old secret stops authenticating immediately; client_id stays the same.",
    clientSecretRotated: "New client_secret",
    clientSecretHint:
      "Use this in the OAuth access-token request. client_id is unchanged.",
    quota: "Monthly quota",
    webhook: "Webhook URL",
    isWeb: "Web",
    isMobile: "Mobile",
    secretWarning: "This secret is shown only NOW. Save it!",
    secretCopied: "Copied",
    createSuccess: "Application created",
    threshold: "Similarity",
    thresholdEdit: "Similarity threshold",
    thresholdHint:
      "Minimum face-match score to pass verification (0.55–0.99). Higher is stricter. Default 0.65.",
    securityTitle: "Application security settings",
    securitySuccess: "Application settings saved",
    noChanges: "Nothing changed",
    checkSpoofHint:
      "Passive anti-spoof model and the deepfake/screen screen. Off: a printed photo or a phone screen is no longer rejected.",
    checkLivenessHint:
      "The active head-turn challenges. Off: the SDK receives an EMPTY challenges list — the user is never asked to turn their head.",
    checkFrameTimeHint:
      "Every camera frame is signed by the device and must be within 5 minutes of our server's clock, in either direction, by the DEVICE's own clock. On: a user whose phone or computer has the wrong date or time is always rejected as a tampered frame, and retrying cannot help. Off: frames are still signed and verified cryptographically, only the 5-minute window is not enforced — turn it off for devices whose clock cannot be fixed.",
    checkAppAttestHint:
      "Apple App Attest on iOS captures: Apple vouches that the request came from a genuine, unmodified copy of the host app on a real Apple device. Off: an iOS capture from a repackaged app, a simulator or a script is no longer rejected. Android and web are unaffected either way.",
    challengeTypes: "Head-pose actions",
    challengeTypesHint:
      "Each session is issued 2 at random from the ticked set. Measured in production: pitch fails 3.2x more often than yaw (33.5% and 30.6% against 10.3% and 9.8%) — a phone held at chest height leaves the head naturally tilted down.",
    challengeTypesMin:
      "At least 2 must stay selected. To switch the check off entirely, use the Liveness toggle above.",
    iosAppIds: "iOS App IDs",
    iosAppIdAdd: "Add",
    iosAppIdPlaceholder: "ABCDE12345.uz.company.app",
    iosAppIdsHint:
      "Which Apple App IDs may attest for this application — our SDK ships inside several host apps, each with its own. Format: a 10-character Team ID, a dot, then the bundle id (the Team ID is stored uppercase, the bundle id exactly as typed). Up to 20. An empty list means the server's own default App ID is used.",
    iosAppIdsEmpty: "No list — the server's default App ID is used.",
    iosAppIdInvalid:
      "The format must be TEAMID.bundle.id — a 10-character Team ID (A–Z, 0–9), a dot, then the bundle id. For example ABCDE12345.uz.company.app.",
    iosAppIdDuplicate: "This App ID is already in the list",
    iosAppIdTooLong: "An App ID may be at most 255 characters",
    iosAppIdLimit: "At most 20 App IDs",
    checkOffTitle: "Lowering the security level",
    checkOffConfirmButton: "Yes, turn it off",
    checkSpoofOffConfirm:
      "Turn the spoof check off? After this a printed photo or an image on a phone screen will also pass verification.",
    checkLivenessOffConfirm:
      "Turn the liveness check off? The user is never asked to turn their head, so nothing verifies that a live person is present.",
    checkFrameTimeOffConfirm:
      "Turn the frame time check off? Frames stay signed and cryptographically verified, but a frame will also be accepted long after it was captured, so recorded frames can be replayed into that same session until it expires (10 minutes). Frames from another session are still rejected.",
    checkAppAttestOffConfirm:
      "Turn the App Attest check off? After this an iOS capture is accepted without Apple vouching for the app, so a repackaged copy of your app, a simulator or a script can also submit frames.",
    credentials: "Credentials",
    shareLink: "Share link",
    shareConfirm:
      "Generate a one-time share link? This rotates BOTH client_secret and webhook_secret and invalidates any previous link.",
    shareTitle: "One-time credential link",
    shareHint:
      "The recipient can open this link and reveal the credentials only once. Generating a new link invalidates this one.",
    shareUrl: "Share URL",
    shareExpires: "Expires",
  },
  users: {
    title: "Backoffice users",
    create: "New user",
    edit: "Edit",
    email: "Email",
    invalidEmail: "Invalid email",
    password: "Password",
    newPassword: "New password",
    passwordMin: "At least 8 characters",
    changePassword: "Change password",
    fullName: "Full name",
    role: "Role",
    role_super_admin: "Super admin",
    role_moderator: "Moderator",
    role_bookkeeping: "Bookkeeping",
    role_support: "Support",
    role_readonly: "Read-only",
    role_company_viewer: "Company (view only)",
    active: "Active",
    activeYes: "Active",
    activeNo: "Disabled",
    lastLogin: "Last login",
    you: "you",
    delete: "Delete user",
    deleteConfirm: "Delete {email}? They lose access immediately.",
    createSuccess: "User created",
    updateSuccess: "User updated",
    deleteSuccess: "User deleted",
    passwordSuccess: "Password changed",
  },
  common: {
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    delete: "Delete",
    search: "Search",
    refresh: "Refresh",
    exportCsv: "Export CSV",
    loading: "Loading...",
    empty: "No data",
    requiredField: "This field is required",
    yes: "Yes",
    no: "No",
    locale: "Language",
    loadFailed: "Could not load",
    retry: "Try again",
    totalCapped: "Total {n}+",
    totalCappedHint: "The server stopped counting here, so this is a floor and not an exact total — counting exactly over millions of rows costs more than the page it decorates. The pager ends at this count: to reach rows beyond it, narrow the filters rather than paging further.",
  },
  errors: {
    network: "Network error",
    serverError: "Server error",
    notFound: "Not found",
  },
  share: {
    appLabel: "Application",
    reveal: "Show credentials",
    revealWarning:
      "These credentials are shown only once — copy and store them now.",
    clientId: "client_id",
    clientSecret: "client_secret",
    webhookSecret: "Webhook secret",
    loading: "Loading…",
    consumedTitle: "Already collected",
    consumedBody:
      "These credentials were already retrieved and can't be shown again. Ask the admin to generate a new link.",
    expiredTitle: "Link expired",
    expiredBody: "This link has expired. Ask the admin to generate a new one.",
    invalidTitle: "Invalid link",
    invalidBody:
      "This link is invalid or no longer exists. Ask the admin to generate a new one.",
  },
};
